import { type TemplateVariable } from "@/shared/domain/agents";
import { findPlaceholders } from "@/shared/domain/triggers";

/**
 * One run of the kickoff message as the agent will receive it: text, or a
 * placeholder that is filled or still empty.
 */
export type KickoffSegment =
  /** Message text between placeholders, as written. */
  | { kind: "text"; text: string }
  /** A placeholder with a value; `text` is that value. */
  | { kind: "filled"; name: string; text: string }
  /** A placeholder with nothing to put in it; `text` is the placeholder as written. */
  | { kind: "empty"; name: string; text: string };

/**
 * Splits `template` on its `{{ name }}` placeholders, the grammar the server
 * substitutes, swapping each for its value in `values`. A blank value leaves
 * the placeholder empty, as a blank required variable is refused.
 */
export function substituteVariables(
  template: string,
  values: Readonly<Record<string, string>>,
): KickoffSegment[] {
  const segments: KickoffSegment[] = [];
  let cursor = 0;
  for (const { start, end, name } of findPlaceholders(template)) {
    if (start > cursor) {
      segments.push({ kind: "text", text: template.slice(cursor, start) });
    }
    const value = values[name] ?? "";
    segments.push(
      value.trim() === ""
        ? { kind: "empty", name, text: template.slice(start, end) }
        : { kind: "filled", name, text: value },
    );
    cursor = end;
  }
  if (cursor < template.length) {
    segments.push({ kind: "text", text: template.slice(cursor) });
  }
  return segments;
}

/**
 * The values the agent will start with: what was typed, and for a variable
 * left alone its declared default, which the server substitutes.
 */
export function valuesWithDefaults(
  variables: readonly TemplateVariable[],
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const resolved: Record<string, string> = {};
  for (const variable of variables) {
    const typed = values[variable.name] ?? "";
    const value = typed.trim() === "" ? variable.default : typed;
    if (value !== undefined && value !== null && value !== "") {
      resolved[variable.name] = value;
    }
  }
  return resolved;
}
